import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Badge, Box, IconButton, Tooltip } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "../../../config/constants";
import { useCartStore } from "../../../store/useCartStore";

import { navTokens } from "../navbar/navTokens";

interface CartIconProps {
  size?: "small" | "medium" | "large";
  showTooltip?: boolean;
  onClick?: (event?: React.MouseEvent) => void;
  variant?: "button" | "icon";
}

const CartIcon: React.FC<CartIconProps> = ({
  size = "medium",
  showTooltip = true,
  onClick,
  variant = "button",
}) => {
  const navigate = useNavigate();
  const { t: myTranslate } = useTranslation();
  const { getItemCount } = useCartStore();

  const handleClick = (event: React.MouseEvent) => {
    // Stop event propagation to prevent parent click events
    event.stopPropagation();

    if (onClick) {
      onClick(event);
    } else {
      navigate(ROUTES.CART);
    }
  };

  const iconSize = {
    small: 20,
    medium: 24,
    large: 32,
  }[size];

  const badgeSize = {
    small: { fontSize: 7, height: 12, minWidth: 12 },
    medium: { fontSize: 9, height: 16, minWidth: 16 },
    large: { fontSize: 11, height: 20, minWidth: 20 },
  }[size];

  const badge = (
    <Badge
      badgeContent={getItemCount()}
      color="error"
      sx={{
        "& .MuiBadge-badge": {
          fontSize: badgeSize.fontSize,
          height: badgeSize.height,
          minWidth: badgeSize.minWidth,
          backgroundColor: "#EF4444",
          color: "#FFFFFF",
          boxShadow: "0 0 0 2px #FFFFFF",
        },
      }}
    >
      <ShoppingBagOutlinedIcon sx={{ fontSize: iconSize }} />
    </Badge>
  );

  const buttonStyles = {
    color: navTokens.textMuted,
    p: 0.75,
    borderRadius: "12px",
    border: `1px solid ${navTokens.border}`,
    backgroundColor: navTokens.bg,
    transition: "all 0.2s ease",
    "&:hover": {
      color: navTokens.primary,
      borderColor: navTokens.primary,
      backgroundColor: navTokens.bgMuted,
    },
  } as const;

  const iconStyles = {
    color: "inherit",
    p: 0,
    borderRadius: "12px",
    transition: "color 0.2s ease",
    "&:hover": {
      color: navTokens.primary,
    },
  } as const;

  const icon =
    variant === "button" ? (
      <IconButton onClick={handleClick} sx={buttonStyles}>
        {badge}
      </IconButton>
    ) : (
      <Box
        component="span"
        sx={{
          ...iconStyles,
          display: "inline-flex",
          "&:hover": onClick ? iconStyles["&:hover"] : undefined,
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={(event: React.MouseEvent) => {
          if (!onClick) return;
          event.stopPropagation();
          onClick(event);
        }}
      >
        {badge}
      </Box>
    );

  if (showTooltip) {
    return (
      <Tooltip title={myTranslate("cart.cartItems", { count: getItemCount() })}>
        {icon}
      </Tooltip>
    );
  }

  return icon;
};

export default CartIcon;
